import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Truck, Star, Clock, Award, Maximize2, ChevronLeft, ChevronRight, X } from 'lucide-react';
const stats = [{
  label: 'Years Serving WA',
  value: '10+',
  icon: Clock
}, {
  label: 'Deliveries Made',
  value: '5,000+',
  icon: Truck
}, {
  label: 'Locally Owned',
  value: '100%',
  icon: Award
}, {
  label: 'Customer Rating',
  value: '5-Star',
  icon: Star
}];
const gallery = [{
  url: 'https://cdn.magicpatterns.com/uploads/gju6hVSPcTW63PScwXbTpM/file_00000000afac71fd869ff743c7d17b4b~2.png',
  caption: "Sun's out, Trucks out — Early morning jobsite delivery.",
  className: 'md:col-span-2 md:row-span-2'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/sjEYLcxbSQCV5uPqLy47XR/topsoil_delivery_tacoma.png',
  caption: 'Topsoil Delivery in Tacoma',
  className: 'md:col-span-1 md:row-span-1'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/htSTMZ7eXmWbKLix9BY7Uz/fll_dirt_delivery_tacoma.png',
  caption: 'Fill Dirt Delivery in Tacoma',
  className: 'md:col-span-1 md:row-span-1'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/5XaxepFncit8gaVhvc86Zp/253_gravel_tacoma_premier_gravel_supplier.png',
  caption: '253 Gravel Driveway Delivery',
  className: 'md:col-span-1 md:row-span-1'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/f6KjU1eZLCASRyutXodR93/dump_truck_picking_up_gravel_in_Olympia_WA.png',
  caption: 'Picking Up Gravel at the Quarry',
  className: 'md:col-span-1 md:row-span-1'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/eJQQFYrPpWBUcLecKVCCbg/natural_rock_delivery_in_graham.png',
  caption: 'Natural Rock Delivery in Graham',
  className: 'md:col-span-1 md:row-span-1'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/39s3YjBVrxHA58hpuYK4z7/PXL_20260430_150724295~2.jpg',
  caption: 'Ah, the Smell of Fresh Topsoil Early in the Morning',
  className: 'md:col-span-1 md:row-span-1'
}];
export function About() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev! + 1) % gallery.length);
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev! - 1 + gallery.length) % gallery.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex]);
  return <div className="bg-[#f3f0ec] dark:bg-[#292827] transition-colors duration-300 min-h-screen" data-id="element-56">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#f3f0ec] dark:bg-[#292827] overflow-hidden" style={{
      minHeight: '700px'
    }} data-id="element-57">
        {/* Background Image */}
        <div className="absolute inset-0" data-id="element-58">
          <img src="https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png" alt="Dump trucks at gravel yard" className="w-full h-full object-cover" data-id="element-59" />
          <div className="absolute inset-0 bg-black/30" data-id="element-60" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-61">
          <div className="text-center max-w-3xl mx-auto" data-id="element-62">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1 px-3 rounded-xl bg-brand-orange/20 text-brand-orange text-sm font-bold tracking-wider uppercase mb-6" data-id="element-63">
              Our Story
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-64">
              Building the foundation of our community,{' '}
              <span className="text-brand-orange" data-id="element-65">one load at a time.</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-200 dark:text-stone-300 leading-relaxed" data-id="element-66">
              We are 253Gravel — your trusted local partner for premium
              landscaping materials and dependable delivery across the South
              Sound.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-20 -mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-67">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-68">
          {stats.map((stat, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1,
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} whileHover={{
          y: -8,
          transition: {
            type: 'spring',
            stiffness: 400,
            damping: 20
          }
        }} className="relative bg-white dark:bg-[#474747] rounded-2xl p-6 md:p-8 text-center cursor-default group border border-stone-200 dark:border-[#555555] hover:border-brand-orange/40 dark:hover:border-brand-orange/40 transition-all duration-300 overflow-hidden shadow-md hover:shadow-xl dark:shadow-lg dark:hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)]" data-id="element-69">
              {/* Large watermark number */}
              <span className="absolute -top-4 -right-2 font-heading text-[80px] font-black text-stone-900/[0.03] dark:text-white/[0.03] group-hover:text-brand-orange/[0.06] transition-colors duration-500 pointer-events-none select-none leading-none" data-id="element-70">
                {stat.value}
              </span>

              <div className="relative z-10" data-id="element-71">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-orange/10 text-brand-orange mb-5 transition-all duration-300 group-hover:bg-brand-orange/20 group-hover:shadow-[0_0_24px_rgba(192,178,131,0.25)] group-hover:scale-110" data-id="element-72">
                  <stat.icon className="w-7 h-7 transition-transform duration-300 group-hover:scale-110" data-id="element-73" />
                </div>
                <div className="text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white mb-2 font-heading transition-colors duration-300 group-hover:text-brand-orange tracking-tight" data-id="element-74">
                  {stat.value}
                </div>
                <div className="text-xs font-bold text-stone-700 dark:text-stone-200 uppercase tracking-[0.15em] transition-colors duration-300 group-hover:text-stone-700 dark:group-hover:text-stone-200" data-id="element-75">
                  {stat.label}
                </div>
              </div>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-brand-orange/0 group-hover:bg-brand-orange/60 transition-all duration-300" data-id="element-76" />
            </motion.div>)}
        </div>
      </section>

      {/* Story Section */}
      <section id="our-story" className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-[140px]" data-id="element-77">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center" data-id="element-78">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-5" data-id="element-79">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-stone-900 dark:text-white leading-tight mb-6" data-id="element-80">
              More than just gravel. We deliver reliability.
            </h2>
            <div className="w-20 h-1.5 bg-brand-orange rounded-full mb-8" data-id="element-81"></div>
            <p className="text-xl text-stone-600 dark:text-stone-300 font-medium leading-relaxed font-sub" data-id="element-82">
              Founded with a simple mission — to make ordering and receiving
              landscaping materials as easy as possible.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-7 space-y-6 text-lg text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-83">
            <p data-id="element-84">
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-85">
                253Gravel
              </strong>{' '}
              is a locally owned gravel delivery company serving the greater
              Tacoma and King County area. We specialize in bulk delivery of{' '}
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-86">
                crushed rock, drain rock, pea gravel, road base, beauty bark,
                topsoil, and recycled aggregates
              </strong>{' '}
              — everything you need for driveways, landscaping, drainage, and
              construction projects.
            </p>
            <p data-id="element-87">
              We've built our reputation on fast delivery, fair pricing, and
              outstanding customer service. From a single 5-yard load of crushed
              gravel for your driveway to 20+ yards of topsoil for a commercial
              landscaping job, no order is too small or too large.
            </p>
            <p data-id="element-88">
              We deliver throughout{' '}
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-89">
                Tacoma, Puyallup, Bonney Lake, Lakewood, Federal Way, Auburn,
                Kent, Sumner, Edgewood, Milton, and surrounding communities
              </strong>
              . Our experienced dump truck operators know the area and will
              place your material exactly where you need it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Project Gallery Section */}
      <section id="gallery" className="py-24 lg:py-32 bg-stone-100 dark:bg-[#1b1b1b] border-t border-stone-200 dark:border-[#333333] scroll-mt-[140px] relative overflow-hidden" data-id="element-90">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-brand-orange/5 to-transparent pointer-events-none" data-id="element-91" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-92" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-93">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8" data-id="element-94">
            <div className="max-w-2xl" data-id="element-95">
              <div className="flex items-center gap-4 mb-6" data-id="element-96">
                <div className="h-px w-12 bg-brand-orange" data-id="element-97" />
                <span className="text-brand-orange font-bold tracking-[0.2em] uppercase text-sm" data-id="element-98">
                  Portfolio
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-stone-900 dark:text-white mb-6 leading-tight" data-id="element-99">
                Our Work in{' '}
                <span className="dark:text-stone-500 italic font-light text-[#828282]" data-id="element-100">
                  Action
                </span>
              </h2>
              <p className="text-lg text-stone-600 dark:text-stone-400 leading-relaxed font-sub" data-id="element-101">
                From residential driveways to large commercial sites, see how
                our premium materials and reliable delivery bring projects to
                life.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-3 pb-2" data-id="element-102">
              <span className="text-4xl font-heading font-bold text-stone-900 dark:text-white" data-id="element-103">
                {gallery.length}
              </span>
              <span className="text-sm font-bold text-stone-500 uppercase tracking-widest leading-tight" data-id="element-104">
                Featured
                <br data-id="element-105" />
                Projects
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 auto-rows-[300px] md:auto-rows-[350px]" data-id="element-106">
            {gallery.map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7,
            delay: idx * 0.1,
            ease: [0.21, 0.47, 0.32, 0.98]
          }} onClick={() => setLightboxIndex(idx)} className={`group relative overflow-hidden bg-stone-200 dark:bg-[#2a2a2a] cursor-pointer border border-stone-200 dark:border-white/5 hover:border-brand-orange/40 dark:hover:border-brand-orange/30 transition-all duration-500 shadow-lg hover:shadow-2xl ${item.className}`} data-id="element-107">
                <img src={item.url} alt={item.caption} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" data-id="element-108" />

                {/* Hover Overlays */}
                <div className="absolute inset-0 bg-brand-orange/0 group-hover:bg-brand-orange/10 mix-blend-overlay transition-colors duration-500" data-id="element-109" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" data-id="element-110" />

                {/* Content */}
                <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-111">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out" data-id="element-112">
                    <div className="w-8 h-1 bg-brand-orange mb-4 scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500 delay-100" data-id="element-113" />
                    <h3 className="text-white font-heading font-bold text-xl md:text-2xl leading-tight mb-2" data-id="element-114">
                      {item.caption}
                    </h3>
                    <div className="flex items-center gap-2 text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200" data-id="element-115">
                      <span className="text-sm font-bold tracking-wider uppercase" data-id="element-116">
                        View Project
                      </span>
                      <Maximize2 className="w-4 h-4" data-id="element-117" />
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-118">
        {lightboxIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl" data-id="element-119">
            {/* Close Button */}
            <button onClick={() => setLightboxIndex(null)} className="absolute top-6 right-6 z-50 p-3 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-120">
              <X className="w-6 h-6" data-id="element-121" />
            </button>

            {/* Navigation Buttons */}
            <button onClick={e => {
          e.stopPropagation();
          setLightboxIndex(prev => (prev! - 1 + gallery.length) % gallery.length);
        }} className="absolute left-6 z-50 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors hidden md:block" data-id="element-122">
              <ChevronLeft className="w-8 h-8" data-id="element-123" />
            </button>
            <button onClick={e => {
          e.stopPropagation();
          setLightboxIndex(prev => (prev! + 1) % gallery.length);
        }} className="absolute right-6 z-50 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors hidden md:block" data-id="element-124">
              <ChevronRight className="w-8 h-8" data-id="element-125" />
            </button>

            {/* Image Container */}
            <div className="relative w-full h-full flex flex-col items-center justify-center p-4 md:p-12" onClick={() => setLightboxIndex(null)} data-id="element-126">
              <AnimatePresence mode="wait" data-id="element-127">
                <motion.div key={lightboxIndex} initial={{
              opacity: 0,
              scale: 0.95,
              filter: 'blur(10px)'
            }} animate={{
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              scale: 1.05,
              filter: 'blur(10px)'
            }} transition={{
              duration: 0.4,
              ease: [0.21, 0.47, 0.32, 0.98]
            }} className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-128">
                  <img src={gallery[lightboxIndex].url} alt={gallery[lightboxIndex].caption} className="w-auto h-auto max-w-full max-h-[75vh] object-contain shadow-2xl" data-id="element-129" />
                  <div className="mt-6 text-center" data-id="element-130">
                    <p className="text-brand-orange text-sm font-bold tracking-[0.2em] uppercase mb-2" data-id="element-131">
                      Project {lightboxIndex + 1} of {gallery.length}
                    </p>
                    <h3 className="text-white font-heading font-bold text-2xl md:text-3xl" data-id="element-132">
                      {gallery[lightboxIndex].caption}
                    </h3>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}